import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import TaskStatusIcon from 'processes/status/TaskStatusIcon';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/bs/Tooltip';
import {GRAY_DARK} from 'ui-library/Colors';

export const PROCESS_TASK_STATUS_NAMES = {
  [ProcessTaskStatusType.PENDING]: 'Pending',
  [ProcessTaskStatusType.IN_PROGRESS]: 'In progress',
  [ProcessTaskStatusType.FAILED]: 'Failed',
  [ProcessTaskStatusType.CANCELLED]: 'Cancelled',
  [ProcessTaskStatusType.INVALID]: 'Invalid',
  [ProcessTaskStatusType.COMPLETED]: 'Completed',
  [ProcessTaskStatusType.SKIPPED]: 'Skipped',
};

export function StatusCount({
  status,
  count,
  showStatusName = false,
  padTop = false,
  dataTestAttrs,
  useRows = false,
}: {
  status: ProcessTaskStatusType;
  count: number;
  showStatusName?: boolean;
  padTop?: boolean;
  useRows?: boolean;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  const nameDisplay = `${count} ${PROCESS_TASK_STATUS_NAMES[status].toLowerCase()}`;
  return (
    <Tooltip key={status} disabled={showStatusName} title={nameDisplay} delay={{show: 500, hide: 100}}>
      <div
        className={classNames(css(styles.taskStatusCount), {
          [css(styles.taskStatusCountZero)]: count === 0,
          [css(styles.padStatusDisplayTop)]: padTop,
          [css(styles.statusCountNoRows)]: !useRows,
          [css(styles.statusCountRows)]: useRows,
        })}
      >
        <TaskStatusIcon type={status} />
        <span className={css(styles.taskStatusCountText)} {...getTestProps(dataTestAttrs)}>
          {showStatusName ? nameDisplay : count}
        </span>
      </div>
    </Tooltip>
  );
}

export interface ProcessTaskStatusCount {
  status: {
    __typename: 'ProcessTaskStatusNode';
    statusType: ProcessTaskStatusType;
    id: string;
  };
  count: number;
}

export interface ProcessWithStatusCounts {
  taskStatusCounts: Array<ProcessTaskStatusCount>;
}

export function ProcessTaskStatusCounts({
  process,
  showStatusNames = false,
  wrapContents = false,
  useRows = false,
}: {
  process: ProcessWithStatusCounts;
  showStatusNames?: boolean;
  wrapContents?: boolean;
  useRows?: boolean;
}): JSX.Element {
  const getStatusTypeCount = (statusType: ProcessTaskStatusType): number => {
    let count = 0;
    for (const statusCount of process.taskStatusCounts) {
      if (statusCount.status.statusType === statusType) {
        count += statusCount.count;
      }
    }
    return count;
  };

  return (
    <div
      className={css(
        styles.taskStatusCounts,
        wrapContents && styles.wrappedContents,
        useRows && styles.wrappedContentsWithRows
      )}
    >
      {[
        ProcessTaskStatusType.PENDING,
        ProcessTaskStatusType.IN_PROGRESS,
        ProcessTaskStatusType.FAILED,
        ProcessTaskStatusType.CANCELLED,
        ProcessTaskStatusType.INVALID,
        ProcessTaskStatusType.COMPLETED,
      ].map((status) => (
        <StatusCount
          key={status}
          status={status}
          count={getStatusTypeCount(status)}
          showStatusName={showStatusNames}
          padTop={wrapContents}
          dataTestAttrs={{
            component: 'ProcessTaskStatusCounts',
            element: 'statusCount',
            key: PROCESS_TASK_STATUS_NAMES[status].toLowerCase(),
          }}
          useRows={useRows}
        />
      ))}
    </div>
  );
}

export const styles = StyleSheet.create({
  taskStatusCounts: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  taskStatusCount: {
    alignItems: 'center',
    display: 'flex',
  },
  statusCountNoRows: {
    marginRight: '10px',
  },
  statusCountRows: {
    flexBasis: '33%',
    '@container (max-width: 320px)': {
      flexBasis: '50%',
    },
  },
  taskStatusCountText: {
    minWidth: '2ch',
    fontVariantNumeric: 'tabular-nums',
  },
  taskStatusCountZero: {
    alignItems: 'center',
    color: GRAY_DARK,
    display: 'flex',
    fillOpacity: 0.5,
    filter: `grayscale(100%)`,
  },
  wrappedContents: {
    flexWrap: 'wrap',
  },
  wrappedContentsWithRows: {
    containerType: 'inline-size',
  },
  padStatusDisplayTop: {
    paddingTop: 16,
  },
});
